import { useTranslation } from 'react-i18next';

import type { PlanUnitDetails } from 'components/types';

/**
 * What the card shows for the childcare type, including where the answer comes from.
 *
 * The provenance is the point. Someone looking at a group wants to know whether the type was recorded
 * there or comes from the location above it: the first is something to correct here, the second means
 * changing it here would be an exception. Showing only the type hides that difference.
 *
 * Returns undefined when the server sent nothing — a tenant without the childcare module — and the
 * field then falls back to the dash every empty field on this card shows.
 */
export const useChildcareTypeLabel = (
  planUnitDetails: PlanUnitDetails | null | undefined
): string | undefined => {
  'use memo';
  const { t } = useTranslation('common');

  if (!planUnitDetails?.childcareTypeSource) {
    return undefined;
  }

  const { childcareType, childcareTypeSource, childcareTypeSourceName } =
    planUnitDetails;

  if (childcareTypeSource === 'NONE' || !childcareType) {
    return t('costCenterDetail.childcareTypeNone');
  }

  const typeLabel = t(`costCenterDetail.childcareTypes.${childcareType}`);

  if (childcareTypeSource === 'OWN' || !childcareTypeSourceName) {
    return typeLabel;
  }

  return t('costCenterDetail.childcareTypeInheritedLabel', {
    type: typeLabel,
    name: childcareTypeSourceName
  });
};
